const paths = ['Dev', 'Sups', 'Teachers', 'Students', 'NewAdmins', 'SuperAdmin'];
const roles = {
  Sups: 'Teacher',
  Teachers: 'Teacher',
  Students: 'Student',
  NewAdmins: 'NewAdmin',
  SuperAdmin: 'Superadmin',
};

const failure = code => Object.assign(new Error(code), { code });

// Bounded legacy casing formats, without embedding production identities.
// These are lookup hints, never identity or role evidence.
const legacyCaseCandidates = mail => {
  const [local, domain] = mail.toLowerCase().split('@');
  return [...new Set([[0], [0, 3, 5]].map(positions =>
    `${[...local].map((letter, index) => positions.includes(index) ? letter.toUpperCase() : letter).join('')}@${domain}`,
  ))].filter(key => key !== mail);
};

// No state changes here: callers commit only the current Auth request.
export async function loadAuthProfile(user, read, timeoutMs = 45000) {
  if (!user?.email) throw failure('auth/profile-missing');
  let mail = user.email.replace(/\./g, '');
  let timer;

  try {
    const readKey = key =>
      Promise.all(
        paths.map(async path => {
          try {
            const snapshot = await read(`${path}/${key}`);
            return snapshot.exists() ? { path, data: snapshot.val() } : null;
          } catch (error) {
            const code = String(error?.code || '')
              .toLowerCase()
              .replace(/_/g, '-');
            // Legacy auxiliary branches may be unreadable for regular accounts.
            // Never hide failures in role-bearing branches or network failures.
            if (['Dev', 'Sups'].includes(path) && ['permission-denied', 'database/permission-denied'].includes(code)) {
              return null;
            }
            throw error;
          }
        }),
      );
    const records = await Promise.race([
      (async () => {
        const exact = await readKey(mail);
        // Preserve existing exact-key resolution, including visible role conflicts.
        if (exact.some(Boolean)) return exact;
        const candidates = await Promise.all(legacyCaseCandidates(mail).map(async key => ({ key, records: await readKey(key) })));
        const found = candidates.filter(candidate => candidate.records.some(Boolean));
        if (found.length > 1) throw failure('auth/profile-conflict');
        if (!found.length) return exact;
        // Strict email validation below still applies to every alternate record.
        mail = found[0].key;
        return found[0].records;
      })(),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(failure('auth/profile-timeout')), timeoutMs);
      }),
    ]);
    let matches = records.filter(Boolean);

    if (!matches.length) throw failure('auth/profile-missing');
    if (matches.some(record => record.path === 'Dev')) throw failure('auth/unsupported-role');
    const teacher = matches.find(record => record.path === 'Teachers');
    const validTeacher =
      teacher?.data.type === 'Teacher' &&
      typeof teacher.data.email === 'string' &&
      teacher.data.email.toLowerCase() === user.email.toLowerCase();
    // Only the audited administrative stub shape is unconfirmed. Never merge
    // its fields, grant administrative access, or hide explicit identity errors.
    if (validTeacher) {
      matches = matches.filter(
        ({ path, data }) =>
          !(
            ['NewAdmins', 'SuperAdmin'].includes(path) &&
            (path === 'NewAdmins' ? ['Admin', 'NewAdmin'] : ['Superadmin']).includes(data.type) &&
            !Object.prototype.hasOwnProperty.call(data, 'email') &&
            Object.keys(data).every(field => ['FIO', 'type'].includes(field))
          ),
      );
    }
    for (const record of matches) {
      const auxiliarySup = record.path === 'Sups' && validTeacher;
      const allowed =
        record.path === 'NewAdmins'
          ? ['Admin', 'NewAdmin']
          : record.path === 'Sups'
            ? ['Teacher', 'Sups']
            : [roles[record.path]];
      if (!(auxiliarySup && record.data.type == null) && !allowed.includes(record.data.type)) {
        throw failure('auth/unsupported-role');
      }
      // A shared key alone does not prove that Sups is the same person.
      if (
        !(auxiliarySup && record.data.email == null) &&
        (typeof record.data.email !== 'string' || record.data.email.toLowerCase() !== user.email.toLowerCase())
      ) {
        throw failure('auth/profile-conflict');
      }
    }
    if (new Set(matches.map(record => roles[record.path])).size !== 1) {
      throw failure('auth/profile-conflict');
    }
    // Historical Sups may omit identity fields only beside a validated Teacher.
    const primary = matches.find(record => record.path === 'Teachers') || matches[0];
    return {
      ...primary.data,
      type: primary.path === 'NewAdmins' ? primary.data.type : roles[primary.path],
      email: user.email,
      photoURL: user.photoURL,
      mail,
    };
  } finally {
    clearTimeout(timer);
  }
}
